import { PaymentMethod, MeioPagamento } from '@/types';
import { apiService } from './apiService';
import { API_CONFIG } from '@/config/apiConfig';
import { generateId, storePaymentMethods, getPaymentMethods } from './storageService';

interface ApiPaymentMethod {
  ID: number;
  Descricao: string;
  FormaPagamentoID: number;
  IndicadoPagamento: number;
  QuantidadeParcelas: number;
  DiasIntervalo: number;
  PeridiocidadePagamento: number;
  MeioPagamento: number;
}

const mapPaymentMethodType = (meioPagamento: MeioPagamento): 'cash' | 'credit' | 'debit' | 'pix' | 'other' => {
  switch (meioPagamento) {
    case MeioPagamento.Dinheiro:
      return 'cash';
    case MeioPagamento.CartaoCredito:
      return 'credit';
    case MeioPagamento.CartaoDebito:
      return 'debit';
    case MeioPagamento.Pix:
      return 'pix';
    default:
      return 'other';
  }
};

const mapApiPaymentMethodToPaymentMethod = (apiMethod: ApiPaymentMethod): PaymentMethod => {
  return {
    id: String(apiMethod.ID),
    name: apiMethod.Descricao,
    description: `${apiMethod.Descricao}`,
    enabled: true,
    ID: apiMethod.ID,
    Descricao: apiMethod.Descricao,
    FormaPagamentoID: apiMethod.FormaPagamentoID,
    IndicadoPagamento: apiMethod.IndicadoPagamento,
    QuantidadeParcelas: apiMethod.QuantidadeParcelas,
    DiasIntervalo: apiMethod.DiasIntervalo,
    PeridiocidadePagamento: apiMethod.PeridiocidadePagamento,
    MeioPagamento: apiMethod.MeioPagamento,
    type: mapPaymentMethodType(apiMethod.MeioPagamento as MeioPagamento)
  };
};

// Get all payment methods from API
export const fetchPaymentMethods = async (isPDV: boolean = true): Promise<PaymentMethod[]> => {
  try {
    if (!API_CONFIG.baseURL) {
      return getDefaultPaymentMethods();
    }
    
    const url = `${API_CONFIG.endpoints.paymentMethods.list}?isPDV=${isPDV.toString().toLowerCase()}`;
    const response = await apiService.get<ApiPaymentMethod[]>(url);
    
    if (!response || !Array.isArray(response)) {
      return getDefaultPaymentMethods();
    }
    
    return response.map(mapApiPaymentMethodToPaymentMethod);
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    return getDefaultPaymentMethods();
  }
};

// Sync payment methods with the API
export const syncPaymentMethods = async (isPDV: boolean = true): Promise<boolean> => {
  try {
    console.log('Starting payment methods synchronization...');
    
    // Check if API is configured
    if (!API_CONFIG.baseURL) {
      console.log('API not configured, using default payment methods');
      const defaultMethods = getDefaultPaymentMethods();
      await storePaymentMethods(defaultMethods);
      return true;
    }
    
    // Fetch payment methods from API
    const paymentMethods = await fetchPaymentMethods(isPDV);
    
    if (paymentMethods && paymentMethods.length > 0) {
      console.log(`Fetched ${paymentMethods.length} payment methods from API`);
      
      // Get current payment methods for comparison
      const currentMethods = await getPaymentMethods();
      
      // Check if there are any changes
      const hasChanges = JSON.stringify(currentMethods) !== JSON.stringify(paymentMethods);
      
      if (hasChanges) {
        console.log('Changes detected in payment methods, updating storage');
        await storePaymentMethods(paymentMethods);
        return true;
      } else {
        console.log('No changes in payment methods');
        return false;
      }
    } else {
      console.log('No payment methods received from API');
      return false;
    }
  } catch (error) {
    console.error('Error synchronizing payment methods:', error);
    return false;
  }
};

// Get default payment methods for development
const getDefaultPaymentMethods = (): PaymentMethod[] => {
  return [
    {
      id: generateId(),
      name: 'Dinheiro',
      description: 'Pagamento em dinheiro',
      enabled: true,
      type: 'cash',
      MeioPagamento: MeioPagamento.Dinheiro
    },
    {
      id: generateId(),
      name: 'Cartão de Crédito',
      description: 'Pagamento com cartão de crédito',
      enabled: true,
      type: 'credit',
      MeioPagamento: MeioPagamento.CartaoCredito
    },
    {
      id: generateId(),
      name: 'Cartão de Débito',
      description: 'Pagamento com cartão de débito',
      enabled: true,
      type: 'debit',
      MeioPagamento: MeioPagamento.CartaoDebito
    },
    {
      id: generateId(),
      name: 'PIX',
      description: 'Pagamento via PIX',
      enabled: true,
      type: 'pix',
      MeioPagamento: MeioPagamento.Pix
    }
  ];
};

// Get a payment method by ID
export const fetchPaymentMethodById = async (id: string, isPDV: boolean = true): Promise<PaymentMethod | null> => {
  try {
    if (!API_CONFIG.baseURL) {
      const defaultMethods = getDefaultPaymentMethods();
      return defaultMethods.find(m => m.id === id) || null;
    }
    
    const baseUrl = API_CONFIG.endpoints.paymentMethods.byId.replace(':id', id);
    const url = `${baseUrl}?isPDV=${isPDV.toString().toLowerCase()}`;
    const response = await apiService.get<ApiPaymentMethod>(url);
    
    if (!response) {
      return null;
    }
    
    return mapApiPaymentMethodToPaymentMethod(response);
  } catch (error) {
    console.error(`Error fetching payment method with ID ${id}:`, error);
    return null;
  }
};
